// Чистый reducer + типы действий для AppState. Не импортирует src/core.
import type { AppState, Currency, CurrencyDef, Segment, TripVariant } from '../types';
import { BASE_CURRENCY, CURRENCY_CODE_RE, MAX_VARIANTS } from '../types';
import { seedState } from './seed';
import { VARIANT_COLORS } from './colors';

export { VARIANT_COLORS };

export interface Actions {
  addSegment(seg: Segment): void;
  addSegments(segs: Segment[]): void;
  updateSegment(id: string, patch: Partial<Segment>): void;
  deleteSegment(id: string): void;
  addVariant(name?: string): void;
  renameVariant(id: string, name: string): void;
  deleteVariant(id: string): void;
  setVariantColor(id: string, color: string): void;
  duplicateVariant(id: string, name?: string): void;
  moveVariant(from: number, to: number): void;
  setVariantLocked(id: string, locked: boolean): void;
  setVariantCollapsed(id: string, collapsed: boolean): void;
  addToVariant(variantId: string, segmentId: string, index?: number): void;
  removeFromVariant(variantId: string, segmentId: string): void;
  moveInVariant(variantId: string, from: number, to: number): void;
  setTravelers(n: number): void;
  setDisplayCurrency(c: Currency): void;
  addCurrency(def: CurrencyDef): void;
  updateCurrency(code: Currency, patch: Partial<Omit<CurrencyDef, 'code'>>): void;
  removeCurrency(code: Currency): void;
  replaceState(s: AppState): void;
  resetToSeed(): void;
}

export type StoreAction =
  | { type: 'addSegment'; seg: Segment }
  | { type: 'addSegments'; segs: Segment[] }
  | { type: 'updateSegment'; id: string; patch: Partial<Segment> }
  | { type: 'deleteSegment'; id: string }
  | { type: 'addVariant'; name?: string }
  | { type: 'renameVariant'; id: string; name: string }
  | { type: 'deleteVariant'; id: string }
  | { type: 'setVariantColor'; id: string; color: string }
  | { type: 'duplicateVariant'; id: string; name?: string }
  | { type: 'moveVariant'; from: number; to: number }
  | { type: 'setVariantLocked'; id: string; locked: boolean }
  | { type: 'setVariantCollapsed'; id: string; collapsed: boolean }
  | { type: 'addToVariant'; variantId: string; segmentId: string; index?: number }
  | { type: 'removeFromVariant'; variantId: string; segmentId: string }
  | { type: 'moveInVariant'; variantId: string; from: number; to: number }
  | { type: 'setTravelers'; n: number }
  | { type: 'setDisplayCurrency'; c: Currency }
  | { type: 'addCurrency'; def: CurrencyDef }
  | { type: 'updateCurrency'; code: Currency; patch: Partial<Omit<CurrencyDef, 'code'>> }
  | { type: 'removeCurrency'; code: Currency }
  | { type: 'replaceState'; state: AppState }
  | { type: 'resetToSeed' };

let idCounter = 0;

/** Стабильные короткие id. crypto.randomUUID при наличии, иначе base36-фоллбэк. */
export function newId(prefix?: string): string {
  idCounter += 1;
  const suffix =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix ?? 'seg'}_${idCounter}_${suffix}`;
}

/** Только для тестов: сбрасывает счётчик newId, чтобы id были предсказуемы. */
export function __resetIdCounter(): void {
  idCounter = 0;
}

function withVariantList(
  state: AppState,
  variantId: string,
  fn: (v: TripVariant) => TripVariant,
): AppState {
  let changed = false;
  const variants = state.variants.map((v) => {
    if (v.id !== variantId) return v;
    changed = true;
    return fn(v);
  });
  if (!changed) return state;
  return { ...state, variants };
}

/** Замок варианта. Единственный источник правды по «можно ли менять». */
export function isVariantLocked(state: AppState, variantId: string): boolean {
  return state.variants.some((v) => v.id === variantId && v.locked === true);
}

/**
 * Сегмент входит хотя бы в один заблокированный вариант — значит его нельзя
 * ни редактировать, ни удалять: иначе замок молча пропускал бы изменения
 * содержимого варианта «с чёрного хода», через библиотеку.
 */
export function isSegmentLocked(state: AppState, segmentId: string): boolean {
  return state.variants.some((v) => v.locked === true && v.segmentIds.includes(segmentId));
}

/** Есть ли в библиотеке хоть одна цена в этой валюте. */
export function isCurrencyInUse(state: AppState, code: Currency): boolean {
  return Object.values(state.library).some((seg) => {
    const money = seg.kind === 'lodging' ? seg.pricePerNight : seg.price;
    return money.currency === code;
  });
}

/** Как withVariantList, но изменение заблокированного варианта — no-op. */
function withUnlockedVariant(
  state: AppState,
  variantId: string,
  fn: (v: TripVariant) => TripVariant,
): AppState {
  if (isVariantLocked(state, variantId)) return state;
  return withVariantList(state, variantId, fn);
}

function moveInList(list: readonly string[], from: number, to: number): string[] {
  if (from < 0 || from >= list.length) return list.slice();
  const clampedTo = Math.max(0, Math.min(to, list.length - 1));
  const copy = list.slice();
  const [item] = copy.splice(from, 1);
  if (item === undefined) return list.slice();
  copy.splice(clampedTo, 0, item);
  return copy;
}

function insertIntoList(list: readonly string[], segmentId: string, index: number | undefined): string[] {
  const copy = list.slice();
  const currentIdx = copy.indexOf(segmentId);
  if (currentIdx !== -1) copy.splice(currentIdx, 1);
  const insertAt =
    index === undefined || index < 0 || index > copy.length ? copy.length : index;
  copy.splice(insertAt, 0, segmentId);
  return copy;
}

export function reducer(state: AppState, action: StoreAction): AppState {
  switch (action.type) {
    case 'addSegment': {
      return {
        ...state,
        library: { ...state.library, [action.seg.id]: action.seg },
      };
    }
    case 'addSegments': {
      if (action.segs.length === 0) return state;
      const library = { ...state.library };
      for (const seg of action.segs) {
        library[seg.id] = seg;
      }
      return { ...state, library };
    }
    case 'updateSegment': {
      const existing = state.library[action.id];
      if (!existing) return state;
      if (isSegmentLocked(state, action.id)) return state;
      const updated = { ...existing, ...action.patch } as Segment;
      return { ...state, library: { ...state.library, [action.id]: updated } };
    }
    case 'deleteSegment': {
      const target = state.library[action.id];
      if (!target) return state;
      if (isSegmentLocked(state, action.id)) return state;
      const library = { ...state.library };
      delete library[action.id];

      // Снять linkedTo у партнёра удаляемого рейса.
      if (target.kind === 'flight' && target.linkedTo) {
        const partner = library[target.linkedTo];
        if (partner && partner.kind === 'flight' && partner.linkedTo === action.id) {
          library[partner.id] = { ...partner, linkedTo: undefined };
        }
      }

      // Удалить id из segmentIds всех вариантов. Baggage-сироты НЕ трогаем.
      const variants = state.variants.map((v) => {
        if (!v.segmentIds.includes(action.id)) return v;
        return { ...v, segmentIds: v.segmentIds.filter((sid) => sid !== action.id) };
      });

      return { ...state, library, variants };
    }
    case 'addVariant': {
      if (state.variants.length >= MAX_VARIANTS) return state;
      const color = VARIANT_COLORS[state.variants.length % VARIANT_COLORS.length] ?? '#5b7c99';
      const variant: TripVariant = {
        id: newId('v'),
        name: action.name ?? `Вариант ${state.variants.length + 1}`,
        color,
        segmentIds: [],
      };
      return { ...state, variants: [...state.variants, variant] };
    }
    case 'renameVariant': {
      return withUnlockedVariant(state, action.id, (v) => ({ ...v, name: action.name }));
    }
    case 'deleteVariant': {
      if (!state.variants.some((v) => v.id === action.id)) return state;
      if (isVariantLocked(state, action.id)) return state;
      return { ...state, variants: state.variants.filter((v) => v.id !== action.id) };
    }
    case 'setVariantColor': {
      return withUnlockedVariant(state, action.id, (v) => ({ ...v, color: action.color }));
    }
    case 'duplicateVariant': {
      if (state.variants.length >= MAX_VARIANTS) return state;
      const index = state.variants.findIndex((v) => v.id === action.id);
      const source = state.variants[index];
      if (!source) return state;

      // Копия встаёт сразу под оригиналом и всегда открыта и разблокирована:
      // её делают, чтобы править, а не чтобы законсервировать ещё раз.
      const copy: TripVariant = {
        id: newId('v'),
        name: action.name ?? `${source.name} +`,
        color: VARIANT_COLORS[state.variants.length % VARIANT_COLORS.length] ?? source.color,
        segmentIds: source.segmentIds.slice(),
      };
      const variants = state.variants.slice();
      variants.splice(index + 1, 0, copy);
      return { ...state, variants };
    }
    // Порядок вариантов на доске — свойство доски, а не варианта: сами варианты
    // при этом не меняются, поэтому замок перестановке не мешает.
    case 'moveVariant': {
      const { from, to } = action;
      if (from < 0 || from >= state.variants.length) return state;
      const clampedTo = Math.max(0, Math.min(to, state.variants.length - 1));
      if (clampedTo === from) return state;
      const variants = state.variants.slice();
      const [moved] = variants.splice(from, 1);
      if (!moved) return state;
      variants.splice(clampedTo, 0, moved);
      return { ...state, variants };
    }
    // Замок и сворачивание — единственные действия, разрешённые на locked-варианте:
    // иначе его было бы не разблокировать и не свернуть.
    case 'setVariantLocked': {
      return withVariantList(state, action.id, (v) => ({ ...v, locked: action.locked }));
    }
    case 'setVariantCollapsed': {
      return withVariantList(state, action.id, (v) => ({ ...v, collapsed: action.collapsed }));
    }
    case 'addToVariant': {
      return withUnlockedVariant(state, action.variantId, (v) => ({
        ...v,
        segmentIds: insertIntoList(v.segmentIds, action.segmentId, action.index),
      }));
    }
    case 'removeFromVariant': {
      return withUnlockedVariant(state, action.variantId, (v) => ({
        ...v,
        segmentIds: v.segmentIds.filter((sid) => sid !== action.segmentId),
      }));
    }
    case 'moveInVariant': {
      return withUnlockedVariant(state, action.variantId, (v) => ({
        ...v,
        segmentIds: moveInList(v.segmentIds, action.from, action.to),
      }));
    }
    case 'setTravelers': {
      if (!Number.isFinite(action.n)) return state;
      const clamped = Math.max(1, Math.min(20, Math.round(action.n)));
      if (clamped === state.travelers) return state;
      return { ...state, travelers: clamped };
    }
    case 'setDisplayCurrency': {
      // Показывать можно только валюту из справочника — иначе суммы считались бы по курсу 1.
      if (!state.currencies.some((c) => c.code === action.c)) return state;
      return { ...state, displayCurrency: action.c };
    }
    case 'addCurrency': {
      const code = action.def.code.trim().toUpperCase();
      if (!CURRENCY_CODE_RE.test(code)) return state;
      if (state.currencies.some((c) => c.code === code)) return state;
      const rate = action.def.rateToBase;
      if (!Number.isFinite(rate) || !(rate > 0)) return state;
      const def: CurrencyDef = { code, symbol: action.def.symbol.trim(), rateToBase: rate };
      return { ...state, currencies: [...state.currencies, def] };
    }
    case 'updateCurrency': {
      let changed = false;
      const currencies = state.currencies.map((c) => {
        if (c.code !== action.code) return c;
        const nextRate = action.patch.rateToBase;
        // Базовая валюта — точка отсчёта: её курс всегда 1, менять можно только символ.
        const rate =
          c.code === BASE_CURRENCY || nextRate === undefined
            ? c.rateToBase
            : Number.isFinite(nextRate) && nextRate > 0
              ? nextRate
              : c.rateToBase;
        const symbol = action.patch.symbol === undefined ? c.symbol : action.patch.symbol.trim();
        if (rate === c.rateToBase && symbol === c.symbol) return c;
        changed = true;
        return { ...c, rateToBase: rate, symbol };
      });
      if (!changed) return state;
      return { ...state, currencies };
    }
    case 'removeCurrency': {
      if (action.code === BASE_CURRENCY) return state;
      if (action.code === state.displayCurrency) return state;
      if (!state.currencies.some((c) => c.code === action.code)) return state;
      // Валюту, в которой заведены цены, не выкидываем: иначе суммы молча
      // пересчитались бы по курсу 1.
      if (isCurrencyInUse(state, action.code)) return state;
      return { ...state, currencies: state.currencies.filter((c) => c.code !== action.code) };
    }
    case 'replaceState': {
      return action.state;
    }
    case 'resetToSeed': {
      return seedState();
    }
    default:
      return state;
  }
}
